import React from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import { chatStyles, CHAT_COLORS } from './chatStyles';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const PaperclipIcon = SVG_COMPONENTS.PAPERCLIP;
const ChatSendActiveIcon = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_ACTIVE_BUTTON;
const ChatSendInactiveIcon = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_INACTIVE_BUTTON;
const CloseIcon = SVG_COMPONENTS.X_CIRCLE;
const ReplyArrowIcon = RESIDENTS_SVG_COMPONENTS.REPLY_ARROW;

// ============================================================================
// Theme Types
// ============================================================================

export interface ChatInputTheme {
  /** Background color for the input container */
  backgroundColor: string;
  /** Text color for the input */
  textColor: string;
  /** Placeholder text color */
  placeholderColor: string;
  /** Icon color */
  iconColor: string;
  /** Accent color for active states and reply previews */
  accentColor: string;
  /** Background color for attachment previews */
  previewBackgroundColor: string;
}

// Default marketplace theme
export const MARKETPLACE_INPUT_THEME: ChatInputTheme = {
  backgroundColor: CHAT_COLORS.inputBackground,
  textColor: CHAT_COLORS.textPrimary,
  placeholderColor: CHAT_COLORS.textMuted,
  iconColor: CHAT_COLORS.textMuted,
  accentColor: CHAT_COLORS.accentBlueDark,
  previewBackgroundColor: CHAT_COLORS.messageBubbleReceived,
};

// ============================================================================
// Attachment Types
// ============================================================================

export interface AttachmentPreviewItem {
  id?: string;
  name: string;
  uri?: string;
  previewUri?: string;
}

export interface ReplyToInfo {
  id: string;
  text: string;
  senderName: string;
}

// ============================================================================
// Sub-components
// ============================================================================

interface AttachmentPreviewProps {
  attachments: AttachmentPreviewItem[];
  onRemove: (index: number) => void;
  theme: ChatInputTheme;
}

const AttachmentPreviewBar: React.FC<AttachmentPreviewProps> = ({ attachments, onRemove, theme }) => {
  if (attachments.length === 0) return null;

  return (
    <View style={[previewStyles.container, { backgroundColor: theme.previewBackgroundColor }]}>
      {attachments.map((attachment, index) => (
        <View key={attachment.id || index} style={previewStyles.item}>
          <PaperclipIcon width={14} height={14} color={theme.iconColor} />
          <Text style={[previewStyles.fileName, { color: theme.textColor }]} numberOfLines={1}>
            {attachment.name || 'File'}
          </Text>
          <TouchableOpacity
            onPress={() => onRemove(index)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <CloseIcon width={16} height={16} color={theme.iconColor} />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};

interface ReplyPreviewBarProps {
  replyTo: ReplyToInfo;
  onClear: () => void;
  theme: ChatInputTheme;
}

const ReplyPreviewBar: React.FC<ReplyPreviewBarProps> = ({ replyTo, onClear, theme }) => {
  const truncatedText = replyTo.text.length > 100 
    ? `${replyTo.text.substring(0, 100)}...` 
    : replyTo.text;

  return (
    <View style={[previewStyles.replyContainer, { 
      backgroundColor: theme.previewBackgroundColor,
      borderLeftColor: theme.accentColor,
    }]}>
      <View style={previewStyles.replyIconContainer}>
        <ReplyArrowIcon width={16} height={16} color={theme.accentColor} />
      </View>
      <View style={previewStyles.replyContentContainer}>
        <Text style={[previewStyles.replySenderName, { color: theme.accentColor }]} numberOfLines={1}>
          Replying to {replyTo.senderName}
        </Text>
        <Text style={[previewStyles.replyMessageText, { color: theme.iconColor }]} numberOfLines={1}>
          {truncatedText || '(Image)'}
        </Text>
      </View>
      <TouchableOpacity
        style={previewStyles.replyCloseButton}
        onPress={onClear}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <CloseIcon width={16} height={16} color={theme.iconColor} />
      </TouchableOpacity>
    </View>
  );
};

const previewStyles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 4,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
  },
  fileName: {
    flex: 1,
    fontSize: 14,
  },
  // Reply preview styles
  replyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderLeftWidth: 3,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 8,
    gap: 12,
  },
  replyIconContainer: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  replyContentContainer: {
    flex: 1,
    gap: 2,
  },
  replySenderName: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
  },
  replyMessageText: {
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
  },
  replyCloseButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ============================================================================
// Main Component
// ============================================================================

export interface ChatInputBarProps {
  /** Current input value */
  value: string;
  /** Called when input value changes */
  onChangeText: (text: string) => void;
  /** Called when send button is pressed */
  onSend: () => void;
  /** Called when attach button is pressed */
  onAttach?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Maximum input length */
  maxLength?: number;
  /** Ref for the TextInput */
  inputRef?: React.RefObject<TextInput | null>;
  /** Bottom padding */
  paddingBottom?: number;
  /** Whether there are attachments (enables send button) */
  hasAttachments?: boolean;
  /** Called when the input is focused */
  onFocus?: () => void;
  /** Whether the input is in a sending state */
  isSending?: boolean;
  
  // Extended props for file preview
  /** Attachments to preview above the input */
  attachments?: AttachmentPreviewItem[];
  /** Called when an attachment is removed */
  onRemoveAttachment?: (index: number) => void;
  
  // Extended props for reply preview
  /** Reply-to information */
  replyTo?: ReplyToInfo | null;
  /** Called when reply is cleared */
  onClearReply?: () => void;
  
  // Theming
  /** Theme configuration */
  theme?: ChatInputTheme;
  
  // Custom send button
  /** Custom send button text (instead of icon) */
  sendButtonText?: string;
  /** Custom styles for the container */
  containerStyle?: ViewStyle;
}

/**
 * Reusable chat input bar with attach button, text input, and send button.
 * Supports file previews, reply previews, and theming.
 */
export const ChatInputBar: React.FC<ChatInputBarProps> = ({
  value,
  onChangeText,
  onSend,
  onAttach,
  placeholder = 'Your message...',
  maxLength = 1000,
  inputRef,
  paddingBottom = 20,
  hasAttachments = false,
  onFocus,
  isSending = false,
  attachments = [],
  onRemoveAttachment,
  replyTo,
  onClearReply,
  theme = MARKETPLACE_INPUT_THEME,
  sendButtonText,
  containerStyle,
}) => {
  const hasText = value.trim().length > 0;
  const hasFiles = attachments.length > 0 || hasAttachments;
  const canSend = (hasText || hasFiles) && !isSending;

  const handleSend = () => {
    if (canSend) {
      onSend();
    }
  };

  return (
    <View style={containerStyle}>
      {/* Attachment Preview */}
      {attachments.length > 0 && onRemoveAttachment && (
        <AttachmentPreviewBar 
          attachments={attachments} 
          onRemove={onRemoveAttachment}
          theme={theme}
        />
      )}

      {/* Reply Preview */}
      {replyTo && onClearReply && (
        <ReplyPreviewBar 
          replyTo={replyTo} 
          onClear={onClearReply}
          theme={theme}
        />
      )}

      {/* Input Bar */}
      <View style={[chatStyles.inputBar, { paddingBottom }]}>
        {/* Attach Button */}
        {onAttach && (
          <TouchableOpacity
            style={chatStyles.attachButton}
            onPress={onAttach}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <PaperclipIcon width={20} height={20} color={theme.iconColor} />
          </TouchableOpacity>
        )}

        {/* Text Input */}
        <TextInput
          ref={inputRef}
          style={[chatStyles.textInput, { color: theme.textColor }]}
          placeholder={placeholder}
          placeholderTextColor={theme.placeholderColor}
          value={value}
          onChangeText={onChangeText}
          onFocus={onFocus}
          multiline
          maxLength={maxLength}
        />

        {/* Send Button */}
        {sendButtonText ? (
          <TouchableOpacity
            style={[
              inputStyles.textSendButton,
              canSend && inputStyles.textSendButtonActive,
              { backgroundColor: canSend ? theme.accentColor : theme.previewBackgroundColor },
            ]}
            onPress={handleSend}
            disabled={!canSend}
            activeOpacity={0.8}
          >
            <Text style={inputStyles.textSendButtonText}>{sendButtonText}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[
              chatStyles.sendButton,
              canSend ? chatStyles.sendButtonActive : chatStyles.sendButtonInactive,
            ]}
            onPress={handleSend}
            disabled={!canSend}
            activeOpacity={0.8}
          >
            {canSend ? (
              <ChatSendActiveIcon width={18} height={18} />
            ) : (
              <ChatSendInactiveIcon width={18} height={18} />
            )}
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const inputStyles = StyleSheet.create({
  textSendButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    minWidth: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textSendButtonActive: {
    opacity: 1,
  },
  textSendButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
